import { defineStore } from 'pinia'
import { collection } from '@firebase/firestore'
import { useWorkspacesStore } from '~/store/workspaces'
import type { Organization } from '~/types/organizations'
import { Collections } from '~/constants/firebase'
import type { OrganizationRoles } from '~/constants/organizations'

type SelectedOrganization = Organization | null
type UserOrganization = { organization: Organization, role: OrganizationRoles }

export const useOrganizationsStore = defineStore('organizationsStore', () => {
  const userUid = ref<string | null>(null)
  const useOrganizationsCollectionRef = computed(() =>
    userUid.value
      ? collection(
          useGetFirestore(),
          Collections.Users.Organizations.base(userUid.value),
        )
      : null,
  )
  const {
    data: userOrganizations,
    promise: userOrganizationsPromise,
  } = useCollection<UserOrganization>(
    useOrganizationsCollectionRef,
    {
      wait: true,
      ssrKey: userUid.value
        ? Collections.Users.Organizations.base(userUid.value)
        : undefined,
    },
  )
  async function getUserOrganizations() {
    const user = await getCurrentUser()
    userUid.value = user.uid
    await nextTick() // Explain in this issue : https://github.com/vuejs/vuefire/discussions/1451
    await userOrganizationsPromise.value
  }

  const organizations = computed<Organization[]>(() =>
    userOrganizations.value
      .map(organization => organization.organization)
      .sort((a, b) => a.displayName?.localeCompare(
        b.displayName ?? '',
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        }),
      ),
  )

  const selectedOrganization = ref<SelectedOrganization>(null)
  const selectedOrganizationRole = ref<OrganizationRoles | null>(null)
  function setSelectedOrganizationRole() {
    selectedOrganizationRole.value = userOrganizations.value.find(
      ({ organization }) => organization.id === selectedOrganization.value!.id,
    )?.role ?? null
  }
  function setSelectedOrganization(value: SelectedOrganization | null) {
    selectedOrganization.value = value
    if (selectedOrganization.value) {
      setSelectedOrganizationRole()
    }
    else {
      const workspacesStore = useWorkspacesStore()
      workspacesStore.setSelectedWorkspace(null)
      selectedOrganizationRole.value = null
    }

    const { $sentrySetTag } = useNuxtApp()
    $sentrySetTag('organizationId', value?.id)
  }

  // To watch organization changes and apply them directly in interface
  watch(organizations, (newVal) => {
    const selectedOrgIndex = newVal.findIndex(
      org => org.id === selectedOrganization.value?.id,
    )

    if (selectedOrgIndex >= 0) {
      selectedOrganization.value = newVal[selectedOrgIndex]
      // TODO: Check to improve organization role watching
      setSelectedOrganizationRole()
    }
  })

  function $reset() {
    userOrganizations.value = []
    selectedOrganization.value = null
    selectedOrganizationRole.value = null
  }

  return {
    organizations,
    getUserOrganizations,
    selectedOrganization,
    selectedOrganizationRole,
    setSelectedOrganization,
    $reset,
  }
})
